import { Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpContextToken, HttpParams } from '@angular/common/http';
import { concat, defer, of } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { GlobalVariables } from '../../../global-variables';

export interface AnalyticsChoice { id: string; name: string; }
export interface AnalyticsFilters {
  enabled: boolean; organizationId: string | null; organizations: AnalyticsChoice[];
  stores: AnalyticsChoice[]; restrictedToStores: boolean;
}
export interface AnalyticsQuery {
  organizationId: string; storeId?: string; dateFrom: string; dateTo: string;
  interval: 'daily' | 'weekly' | 'monthly'; page?: number; pageSize?: number;
}
export interface AnalyticsRow extends AnalyticsChoice {
  views?: number; image?: string; siteViews?: number; qrScans?: number; itemViews?: number;
}
export interface AnalyticsFreshness {generatedAt?: string; latestEventAt?: string | null;}
export interface AnalyticsReport extends AnalyticsFreshness { rows: AnalyticsRow[]; total: number; page: number; pageSize: number; }
export interface AnalyticsSummary extends AnalyticsFreshness {
  kpis: {siteViews: number; qrScans: number; itemViews: number; unattributedViews: number; organizationOnlyViews: number};
  comparisons: Record<string, {previous: number; percent: number | null; reason: string | null}>;
  visitors: {total: number; new: number; returning: number; newPercent: number; returningPercent: number};
  trend: Array<{date: string; views: number}>; topItems: AnalyticsRow[]; topStores: AnalyticsRow[];
  trackingStartedAt: string | null; timezone: string;
}

export const ANALYTICS_BACKGROUND_REPORT = new HttpContextToken<boolean>(() => false);
type ReportData = AnalyticsSummary | AnalyticsReport;
interface ReportSnapshot {key: string; savedAt: number; data: ReportData;}
const SNAPSHOT_KEY = 'smart-site-analytics-reports-v3';

@Injectable({providedIn: 'root'})
export class SmartSiteAnalyticsService {
  private readonly url = GlobalVariables.appURL + 'smart-site-analytics/';
  constructor(private readonly http: HttpClient) {}
  filters(organizationId?: string) {
    const params = organizationId ? new HttpParams().set('organizationId', organizationId) : new HttpParams();
    return this.http.get<{data: AnalyticsFilters}>(this.url + 'filters', {params}).pipe(map(r => r.data));
  }
  summary(filter: AnalyticsQuery) {return this.read<AnalyticsSummary>('summary', filter);}
  report(kind: 'items' | 'stores', filter: AnalyticsQuery) {return this.read<AnalyticsReport>(kind, filter);}
  // Filters must come from the authenticated filters endpoint before reuse.
  results(kind: 'summary' | 'items' | 'stores', filter: AnalyticsQuery, scope: AnalyticsFilters) {
    return defer(() => this.snapshotKey(kind, filter, scope)).pipe(switchMap(key => {
      const cached = this.snapshots().find(entry => entry.key === key)?.data;
      const fresh = this.read<ReportData>(kind, filter, !!cached).pipe(
        tap({next: data => {if (key) this.remember(key, data);}, error: () => this.forget(key)}),
        map(data => ({data, cached: false})),
      );
      return cached ? concat(of({data: cached, cached: true}), fresh) : fresh;
    }));
  }
  private async snapshotKey(kind: string, filter: AnalyticsQuery, scope: AnalyticsFilters): Promise<string> {
    try {
      const token = localStorage.getItem('authToken');
      if (!token || !scope.enabled || scope.organizationId !== filter.organizationId) return '';
      const input = JSON.stringify([this.url, token, Intl.DateTimeFormat().resolvedOptions().timeZone,
        kind, filter, scope.restrictedToStores, scope.stores]);
      // Keep credentials out of stored keys while separating login sessions.
      const hash = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(input));
      return Array.from(new Uint8Array(hash), byte => byte.toString(16).padStart(2, '0')).join('');
    } catch {return '';}
  }
  private snapshots(): ReportSnapshot[] {
    try {
      const entries: ReportSnapshot[] = JSON.parse(sessionStorage.getItem(SNAPSHOT_KEY) || '[]');
      return entries.filter(entry => entry?.key && Date.now() - entry.savedAt >= 0 && Date.now() - entry.savedAt < 300000
        && entry.data?.generatedAt && (('kpis' in entry.data && Array.isArray(entry.data.trend)) || ('rows' in entry.data && Array.isArray(entry.data.rows))));
    } catch {return [];}
  }
  private remember(key: string, data: ReportData): void {
    const entries = [...this.snapshots().filter(entry => entry.key !== key), {key, savedAt: Date.now(), data}].slice(-8);
    while (JSON.stringify(entries).length > 256000) entries.shift();
    this.saveSnapshots(entries);
  }
  private forget(key: string): void {this.saveSnapshots(this.snapshots().filter(entry => entry.key !== key));}
  private saveSnapshots(entries: ReportSnapshot[]): void {
    try {sessionStorage.setItem(SNAPSHOT_KEY, JSON.stringify(entries));} catch { /* Reporting works without browser storage. */ }
  }
  private read<T>(path: string, filter: AnalyticsQuery, background = false) {
    return this.http.get<{data: T}>(this.url + path, {params: new HttpParams().set('filter', JSON.stringify(filter)), context: new HttpContext().set(ANALYTICS_BACKGROUND_REPORT, background)}).pipe(map(r => r.data));
  }
}
